import { useEffect, useState } from 'react';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { Typography } from '../typography';
import { Button } from '../button';
import { getIsValidAssignedTo, Select, YardPlaceEmployeeBlocksMultiSelect } from '../select';
import {
  ModalBase,
  ModalWrapper,
  ModalFooterButtons,
  ModalBodyCustomColumns,
} from './ModalComponents';
import { useUpdateInventoryMutation, useEmployeesYardOptionsQuery } from '../../hooks';
import { INPUT_ERRORS, MESSAGES } from '../../const';
import { enqueueSnackbar } from '../snackbar';
import { i18n } from '../../i18n';
import { BlockWithMinMaxOptional } from '../../types';
import { useStore } from '../../context';

export const inventoryUpdateModal: any = {};

export const InventoryUpdateModal = () => {
  const {
    state: {
      user: { data: user },
    },
  } = useStore();

  const { mutate } = useUpdateInventoryMutation();
  const [open, setOpen] = useState(false);
  const [yardId, setYardId] = useState<any>();
  const [assignedTo, setAssignedTo] = useState<
    { userId: string; blocks: BlockWithMinMaxOptional[] }[]
  >([{ userId: '', blocks: [] }]);

  const [isSubmitClicked, setIsSubmitClicked] = useState(false);
  const [updateKey, setUpdate] = useState<any>();

  const { data: employeesYardOptions = [] } = useEmployeesYardOptionsQuery(yardId);

  const validators = {
    yardId: yardId,
    assignedTo: assignedTo.map((item) => ({
      userId: !item.userId ? INPUT_ERRORS.REQUIRED.DEFAULT : '',
    })),
  };

  useEffect(() => {
    inventoryUpdateModal.open = () => {
      setYardId(inventoryUpdateModal.yardId);
      setAssignedTo(inventoryUpdateModal.assignedTo || [{ userId: '', blocks: [] }]);

      setIsSubmitClicked(false);
      setUpdate(Math.random());
      setOpen(true);
    };
  }, []);

  const handleOnClose = () => {
    Object.keys(inventoryUpdateModal).forEach((key) => {
      if (key !== 'open') delete inventoryUpdateModal[key];
    });
    setOpen(!open);
  };

  const handleSubmitClick = () => {
    setIsSubmitClicked(true);
    setUpdate(Math.random());

    const isValid = getIsValidAssignedTo(yardId, assignedTo, user);
    if (!isValid) return;

    const submitProps = {
      id: inventoryUpdateModal.id,
      yardId,
      assignedTo,
    };

    mutate(submitProps, {
      onSuccess: () => {
        setOpen(false);
        enqueueSnackbar(MESSAGES.INVENTORY.SUCCESS.UPDATED, 'success');
        inventoryUpdateModal.submitClick();
      },
      onError: () => {
        enqueueSnackbar(MESSAGES.INVENTORY.ERROR.NOT_UPDATED, 'error');
      },
    });
  };

  const handleDeleteClick = (i: number) => {
    setUpdate(Math.random());
    const filteredAssignedTo = assignedTo.filter((_: any, index: any) => i !== index);
    setAssignedTo(filteredAssignedTo);
  };

  const handlePlusClick = () => {
    setUpdate(Math.random());
    setAssignedTo([...assignedTo, { userId: '', blocks: [] }]);
  };

  return (
    <ModalBase id={'inventory-update-modal'} open={open} onClose={handleOnClose}>
      <ModalWrapper>
        <Typography variant={'textL'} weight={'bold'}>
          {i18n.t('pages.modal.inventoryUpdateTitle', { yardName: inventoryUpdateModal.yardName })}
        </Typography>
        {!!yardId &&
          assignedTo.map((item, i) => (
            <ModalBodyCustomColumns key={updateKey + i} columns={'1fr'}>
              <ModalBodyCustomColumns
                key={updateKey + i}
                columns={inventoryUpdateModal.assignedTo?.length <= i ? '1fr max-content' : '1fr'}
              >
                <Select
                  id={`employee-${i}`}
                  label={i18n.t('label.employee')}
                  value={employeesYardOptions.length ? item.userId : ''}
                  options={employeesYardOptions.map((item) => ({
                    ...item,
                    disabled: assignedTo.some((nItem) => nItem.userId === item.id),
                  }))}
                  onSelect={(option: any) => {
                    setAssignedTo(
                      assignedTo.map((item, index) =>
                        i === index ? { ...item, userId: option.id } : item,
                      ),
                    );
                  }}
                  error={isSubmitClicked && !!validators.assignedTo[i]?.userId}
                  helperText={isSubmitClicked && validators.assignedTo[i]?.userId}
                  disabled={inventoryUpdateModal.assignedTo?.length > i}
                />
                {inventoryUpdateModal.assignedTo?.length <= i && (
                  <Button
                    className={'row-button-square'}
                    size={'medium'}
                    data-color={'destructiveSecondary'}
                    isSquare={true}
                    onClick={() => handleDeleteClick(i)}
                  >
                    <DeleteIcon />
                  </Button>
                )}
              </ModalBodyCustomColumns>
              <ModalBodyCustomColumns columns={'1fr'}>
                <YardPlaceEmployeeBlocksMultiSelect
                  yardId={yardId}
                  blocks={item.blocks}
                  setBlocks={(blocks: BlockWithMinMaxOptional[]) => {
                    setAssignedTo(
                      assignedTo.map((item, index) => (i === index ? { ...item, blocks } : item)),
                    );
                  }}
                  isSubmitClicked={isSubmitClicked}
                  withDefaultValues
                />
              </ModalBodyCustomColumns>
              {i === assignedTo.length - 1 && (
                <ModalBodyCustomColumns columns={'max-content'}>
                  <Button size={'medium'} data-color={'secondary'} onClick={handlePlusClick}>
                    {i18n.t('button.addEmployee')}
                  </Button>
                </ModalBodyCustomColumns>
              )}
            </ModalBodyCustomColumns>
          ))}
        <ModalFooterButtons>
          <Button size={'medium'} data-color={'white'} onClick={handleOnClose}>
            {i18n.t('button.cancel')}
          </Button>
          <Button size={'medium'} data-color={'primary'} onClick={handleSubmitClick}>
            {i18n.t('button.edit')}
          </Button>
        </ModalFooterButtons>
      </ModalWrapper>
    </ModalBase>
  );
};
